import React from 'react'
import styled from 'styled-components'
import moment from 'moment'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import { AutoTopupSubscription } from 'redux/auto-topup/autoTopupTypes'

const SubscriptionCard = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem 1rem;
  padding: 0.875rem 0.875rem 0.875rem 1rem;
  overflow: hidden;
  border: 1px solid ${(props) => props.theme.palette.primary.pink100};
  border-radius: 0.75rem;
  background: linear-gradient(
    135deg,
    ${(props) => props.theme.palette.primary.pink50} 0%,
    ${(props) => props.theme.palette.secondary.purple50} 100%
  );

  @media (max-width: ${(props) => props.theme.breakpoints[1]}) {
    grid-template-columns: 1fr;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 0.25rem;
    background-color: ${(props) => props.theme.palette.primary.pink800};
  }
`

const InfoFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
`

const FullWidthFieldContainer = styled.div`
  grid-column: 1 / -1;
`

const ValueText = styled(Text)`
  color: ${(props) => props.theme.palette.secondary.purple900};
`

const StatusPill = styled(Text)`
  width: fit-content;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  color: ${(props) => props.theme.palette.primary.pink800};
  background-color: ${(props) => props.theme.palette.primary.pink100};
`

const formatAmount = (amountCents: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amountCents / 100)

const formatInterval = (interval: string) =>
  interval.charAt(0).toUpperCase() + interval.slice(1)

const formatStatus = (status: string) =>
  status.charAt(0).toUpperCase() + status.slice(1)

const formatNextPaymentDate = (nextPaymentDate: string | null) => {
  if (!nextPaymentDate) {
    return '—'
  }
  return moment(nextPaymentDate).format('MMM D, YYYY')
}

const subscriptionField = (label: string, value: string) => (
  <InfoFieldContainer>
    <Text variant='action3' textColor={palette.text.secondary}>
      {label}
    </Text>
    <ValueText variant='action2'>{value}</ValueText>
  </InfoFieldContainer>
)

const statusField = (status: string) => (
  <InfoFieldContainer>
    <Text variant='action3' textColor={palette.text.secondary}>
      Status
    </Text>
    <StatusPill variant='action4'>{formatStatus(status)}</StatusPill>
  </InfoFieldContainer>
)

interface SubscriptionDetailsProps {
  subscription: AutoTopupSubscription
}

const SubscriptionDetails = React.memo((props: SubscriptionDetailsProps) => {
  const { subscription } = props

  return (
    <SubscriptionCard>
      {subscription.bike?.name ? (
        <FullWidthFieldContainer>
          {subscriptionField('Bike', subscription.bike.name)}
        </FullWidthFieldContainer>
      ) : null}
      {subscriptionField('Amount', formatAmount(subscription.amount))}
      {subscriptionField('Interval', formatInterval(subscription.interval))}
      {subscriptionField(
        'Next payment date',
        formatNextPaymentDate(subscription.nextPaymentDate)
      )}
      {statusField(subscription.status)}
    </SubscriptionCard>
  )
})

if (process.env.NODE_ENV !== 'production') {
  SubscriptionDetails.displayName = 'SubscriptionDetails'
}

export default SubscriptionDetails
